import {
  immutableZkEvm,
  immutableZkEvmTestnet,
  mainnet,
  sepolia,
} from "viem/chains";

import { appConfig } from "@/constants";
import type { MinimalCoinBalance } from "./types";

export const STAKED_IMX_NAME = "Staked IMX";
export const IMX_SYMBOLS = ["imx", "timx", "dimx"];

export const IMX_CONFIG = {
  symbol: "IMX",
  contractAddress: "native",
  decimals: 18,
  balance: "0",
  name: "IMX",
  imageUrl: `${appConfig.TOKEN_ICONS_URL}/imx.svg`,
} as const;

// Chain IDs from viem chains
const IMTBL_ZKEVM_MAINNET_ID = immutableZkEvm.id; // 13371
const IMTBL_ZKEVM_TESTNET_ID = immutableZkEvmTestnet.id; // 13473
// Note: Devnet uses the same chain ID as testnet in viem (13473)
// If checkout SDK uses a different devnet ID, we can add it separately
const ETHEREUM_MAINNET_ID = mainnet.id; // 1
const SEPOLIA_ID = sepolia.id; // 11155111

const zeroBalanceCoins: Record<number, MinimalCoinBalance[]> = {
  [IMTBL_ZKEVM_MAINNET_ID]: [
    {
      symbol: "IMX",
      contractAddress: "native",
      decimals: 18,
      balance: "0",
      name: "IMX",
      imageUrl: `${appConfig.TOKEN_ICONS_URL}/imx.svg`,
    },
  ],
  [IMTBL_ZKEVM_TESTNET_ID]: [
    {
      symbol: "IMX",
      contractAddress: "native",
      decimals: 18,
      balance: "0",
      name: "tIMX",
      imageUrl: `${appConfig.TOKEN_ICONS_URL}/imx.svg`,
    },
    {
      symbol: "GOG",
      contractAddress: "0xb8ee289c64c1a0dc0311364721ada8c3180d838c",
      decimals: 18,
      balance: "0",
      name: "Guild of Guardians",
      imageUrl: `${appConfig.TOKEN_ICONS_URL}/0xb8ee289c64c1a0dc0311364721ada8c3180d838c.svg`,
    },
  ],
  // Devnet uses same chain ID as testnet (13473) - if checkout SDK uses different ID, add it here
  [ETHEREUM_MAINNET_ID]: [],
  [SEPOLIA_ID]: [],
};

const getZeroBalanceCoins = (chainId: number): MinimalCoinBalance[] =>
  zeroBalanceCoins[chainId] || [];

/**
 * Adds a staked IMX entry to the balances list, positioned after regular IMX.
 */
export function addStakedIMXEntry(
  coinBalances: MinimalCoinBalance[],
  stakedBalance: bigint | undefined,
): MinimalCoinBalance[] {
  if (!stakedBalance || stakedBalance === 0n) return coinBalances;

  const stakedIMXEntry: MinimalCoinBalance = {
    ...IMX_CONFIG,
    name: STAKED_IMX_NAME,
    balance: stakedBalance.toString(),
  };

  const imxIndex = coinBalances.findIndex(
    (balance) =>
      balance.symbol === "IMX" && balance.contractAddress === "native",
  );

  // Insert staked balance after IMX if found, otherwise at the beginning
  const newBalances = [...coinBalances];
  const insertIndex = imxIndex >= 0 ? imxIndex + 1 : 0;
  newBalances.splice(insertIndex, 0, stakedIMXEntry);

  return newBalances;
}

export const addZeroBalanceCoins = (
  chainId: number,
  coinBalances: MinimalCoinBalance[],
): MinimalCoinBalance[] => {
  const nonZeroBalances = new Map(
    coinBalances.map((b) => [b.contractAddress.toLowerCase(), true]),
  );

  const zeroBalancesToAdd = getZeroBalanceCoins(chainId).filter(
    (zeroBalance) =>
      !nonZeroBalances.has(zeroBalance.contractAddress.toLowerCase()),
  );

  return [...coinBalances, ...zeroBalancesToAdd];
};
